import { type FC, type ReactNode, useCallback, useState } from 'react';
import { Handle, Position } from 'reactflow';
import type { NodeData } from '@/types/node.types';
import { NODE_STATUS_LABELS } from '@/types/node.types';
import { useCanvasStore } from '@/stores/canvas.store';
import NodeEditPanel from './NodeEditPanel';

interface BaseNodeProps {
  id: string;
  data: NodeData;
  color: string;
  icon: ReactNode;
  selected?: boolean;
}

const handleStyle = (color: string, position: Position): React.CSSProperties => {
  const base: React.CSSProperties = {
    background: color,
    width: 10,
    height: 10,
    border: `2px solid var(--color-surface)`,
    borderRadius: '50%',
    cursor: 'crosshair',
    zIndex: 5,
  };
  // Override position offsets so source+target overlap on the same spot
  if (position === Position.Top) return { ...base, top: -5 };
  if (position === Position.Bottom) return { ...base, bottom: -5 };
  if (position === Position.Left) return { ...base, left: -5 };
  return { ...base, right: -5 };
};

const statusColors: Record<string, string> = {
  Active: '#22C55E',
  Paused: '#F59E0B',
  Archived: '#6B7280',
  ToReview: '#8B5CF6',
};

const toNumber = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const formatEuro = (n: number): string => {
  if (Math.abs(n) >= 1000) {
    const k = n / 1000;
    const rounded = Math.round(k * 10) / 10;
    return `${rounded.toLocaleString('fr-FR')} k€`;
  }
  return `${Math.round(n).toLocaleString('fr-FR')} €`;
};

export const BaseNode: FC<BaseNodeProps> = ({ id, data, color, icon, selected }) => {
  const removeNode = useCanvasStore((s) => s.removeNode);
  const [isEditing, setIsEditing] = useState(false);

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      removeNode(id);
    },
    [id, removeNode],
  );

  const handleEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  }, []);

  return (
    <div
      style={{
        width: 240,
        minHeight: 140,
        position: 'relative',
        backgroundColor: 'var(--color-surface)',
        border: `2px solid ${selected ? color : 'var(--color-border)'}`,
        borderRadius: 'var(--radius-lg)',
        boxShadow: selected ? `0 0 12px ${color}40` : 'var(--shadow-md)',
        overflow: 'visible',
        transition: 'var(--transition-fast)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* 4 handles — each is both source and target */}
      {([Position.Top, Position.Bottom, Position.Left, Position.Right] as const).map((pos) => (
        <Handle
          key={`target-${pos}`}
          id={`${pos}-target`}
          type="target"
          position={pos}
          style={handleStyle(color, pos)}
        />
      ))}
      {([Position.Top, Position.Bottom, Position.Left, Position.Right] as const).map((pos) => (
        <Handle
          key={`source-${pos}`}
          id={`${pos}-source`}
          type="source"
          position={pos}
          style={handleStyle(color, pos)}
        />
      ))}

      {/* Action buttons — top right, always visible when selected */}
      {selected && (
        <div
          style={{
            position: 'absolute',
            top: -12,
            right: -12,
            display: 'flex',
            gap: 4,
            zIndex: 10,
          }}
        >
          {/* Edit button */}
          <button
            onClick={handleEdit}
            title="Modifier"
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              backgroundColor: '#3B82F6',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              lineHeight: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
            }}
          >
            ✎
          </button>
          {/* Delete button */}
          <button
            onClick={handleDelete}
            title="Supprimer"
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              backgroundColor: '#EF4444',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              fontWeight: 700,
              lineHeight: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Color bar */}
      <div style={{ height: 4, backgroundColor: color, borderRadius: '6px 6px 0 0' }} />

      {/* Content */}
      <div
        style={{
          padding: 'var(--spacing-sm) var(--spacing-md)',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-xs)',
          minWidth: 0,
        }}
      >
        {/* Header row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 'var(--spacing-xs)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--spacing-xs)',
              flex: 1,
              minWidth: 0,
            }}
          >
            <span style={{ fontSize: 'var(--font-size-md)', color, lineHeight: 1.2, flexShrink: 0 }}>
              {icon}
            </span>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--color-text)',
                lineHeight: 1.3,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {data.title}
            </span>
          </div>

          {/* Priority badge */}
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 700,
              color: 'var(--color-text)',
              backgroundColor: color,
              borderRadius: 'var(--radius-sm)',
              padding: '1px 6px',
              minWidth: 20,
              textAlign: 'center',
              flexShrink: 0,
              height: 'fit-content',
            }}
          >
            P{data.priority}
          </span>
        </div>

        {/* Description */}
        {data.description && (
          <p
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
              lineHeight: 1.4,
              margin: 0,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {data.description}
          </p>
        )}

        {/* Status indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
          <div
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: statusColors[data.status] || '#6B7280',
            }}
          />
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
            {NODE_STATUS_LABELS[data.status]}
          </span>
        </div>

        <NodeProgressFooter data={data} color={color} />
      </div>


      {/* Edit panel */}
      {isEditing && (
        <NodeEditPanel
          nodeId={id}
          data={data}
          color={color}
          onClose={() => setIsEditing(false)}
        />
      )}
    </div>
  );
};

const formatDays = (n: number): string => {
  const rounded = Math.round(n * 10) / 10;
  return `${rounded.toLocaleString('fr-FR')} j`;
};

const NodeProgressFooter: FC<{ data: NodeData; color: string }> = ({ data, color }) => {
  const progress = toNumber(data.progress);
  const planned = toNumber(data.budgetPlanned);
  const spent = toNumber(data.budgetSpent);
  const effortPlanned = toNumber(data.effortPlannedDays);
  const effortSpent = toNumber(data.effortSpentDays);
  const hasBudget = planned !== null || spent !== null;
  const hasEffort = effortPlanned !== null || effortSpent !== null;
  if (progress === null && !hasBudget && !hasEffort) return null;

  const clamped = progress === null ? null : Math.max(0, Math.min(100, progress));
  const overBudget = planned !== null && spent !== null && spent > planned;
  const overEffort = effortPlanned !== null && effortSpent !== null && effortSpent > effortPlanned;

  return (
    <div
      style={{
        marginTop: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      {clamped !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div
            style={{
              flex: 1,
              height: 4,
              borderRadius: 2,
              backgroundColor: 'var(--color-border)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${clamped}%`,
                height: '100%',
                backgroundColor: color,
                transition: 'width 200ms ease',
              }}
            />
          </div>
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: 'var(--color-text-muted)',
              minWidth: 28,
              textAlign: 'right',
            }}
          >
            {clamped}%
          </span>
        </div>
      )}
      {hasBudget && (
        <div
          style={{
            fontSize: 10,
            color: overBudget ? '#EF4444' : 'var(--color-text-muted)',
            fontWeight: overBudget ? 700 : 500,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span aria-hidden>💶</span>
          <span>
            {spent !== null ? formatEuro(spent) : '—'}
            {' / '}
            {planned !== null ? formatEuro(planned) : '—'}
          </span>
        </div>
      )}
      {hasEffort && (
        <div
          style={{
            fontSize: 10,
            color: overEffort ? '#EF4444' : 'var(--color-text-muted)',
            fontWeight: overEffort ? 700 : 500,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span aria-hidden>🧑‍💼</span>
          <span>
            {effortSpent !== null ? formatDays(effortSpent) : '—'}
            {' / '}
            {effortPlanned !== null ? formatDays(effortPlanned) : '—'}
          </span>
        </div>
      )}
    </div>
  );
};

export default BaseNode;
